import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth'
import { db, auth } from '../../firebase/firebase'
import { t } from '../../components/i18n/i18n'
import LikeButton from '../../components/LikeButton'
import "../../assets/css/common.css"
import "../../assets/css/newsModal.css"
import '../../assets/css/pages.css'
import back from "../../assets/images/elements/aboutHeader.jpg"

const PAGE_SIZE = 6

const stripMarkdown = (md = '') =>
  md
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_~`#>]+/g, '')
    .replace(/\n+/g, ' ')
    .trim()

const formatDate = (ts) => {
  if (!ts) return ''
  return new Date(ts.seconds * 1000).toLocaleDateString('uk-UA', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

const NewsModal = ({ item, onClose, anonUid }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div className='news-modal-overlay' onClick={onClose}>
      <div className='news-modal' onClick={e => e.stopPropagation()}>
        <button className='nf-modal-close-btn' onClick={onClose} aria-label='Закрити'>✕</button>

        {item.photoUrl && (
          <div className='nf-modal-img-wrap'>
            <img src={item.photoUrl} alt={item.title} className='nf-modal-img' />
          </div>
        )}

        <div className='nf-modal-body'>
          <div className='nf-date'>{formatDate(item.createdAt)}</div>
          <h2 className='nf-modal-title'>{item.title}</h2>
          <div className='nf-modal-text nf-md-content'>
            <ReactMarkdown>{item.content || ''}</ReactMarkdown>
          </div>
          <div style={{ marginTop: '20px', borderTop: '1px solid #f0e4d4', paddingTop: '16px' }}>
            <LikeButton itemId={item.id} count={item.likesCount} anonUid={anonUid} />
          </div>
        </div>
      </div>
    </div>
  )
}

const getPageNumbers = (current, total) => {
  const pages = []
  const add = (p) => { if (!pages.includes(p)) pages.push(p) }
  add(1)
  for (let p = current - 1; p <= current + 1; p++) {
    if (p > 1 && p < total) add(p)
  }
  if (total > 1) add(total)
  const withGaps = []
  let prev = 0
  for (const p of pages.sort((a, b) => a - b)) {
    if (prev && p - prev > 1) withGaps.push('…')
    withGaps.push(p)
    prev = p
  }
  return withGaps
}

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null
  return (
    <div className='page-pagination'>
      <button
        className={`page-btn${currentPage === 1 ? '' : ''}`}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >←</button>

      {getPageNumbers(currentPage, totalPages).map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className='page-gap'>…</span>
        ) : (
          <button
            key={p}
            className={`page-btn${p === currentPage ? ' page-btn--active' : ''}`}
            onClick={() => onPageChange(p)}
          >{p}</button>
        )
      )}

      <button
        className='page-btn'
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >→</button>
    </div>
  )
}

export const NewsFeed = () => {
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeItem, setActiveItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [anonUid, setAnonUid] = useState(null)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) { setAnonUid(user.uid) }
      else { const cred = await signInAnonymously(auth); setAnonUid(cred.user.uid) }
    })
    return unsub
  }, [])

  useEffect(() => {
    const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(q, snap => {
      setNews(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      setLoading(false)
    }, () => setLoading(false))
    return unsub
  }, [])

  useEffect(() => { setCurrentPage(1) }, [searchTerm])

  const filteredNews = news.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.trim().toLowerCase())
  )
  const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const pageNews = filteredNews.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <section className='text_container_light'>
      <div className='container_100'>

        <div className='page-hero'>
          <img src={back} alt='' />
          <div className='page-hero__overlay' />
          <h1 className='page-hero__title'>{t('newsfeedTitle')}</h1>
        </div>

        <div className='container boxItems' style={{ marginTop: '30px' }}>
          <div className='page-search-wrap'>
            <input
              type='text'
              placeholder={t('search')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='page-search-input'
            />
          </div>

          {loading && <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>•••</p>}
          {!loading && news.length === 0 && (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>{t('newsfeedEmpty')}</p>
          )}
          {!loading && news.length > 0 && filteredNews.length === 0 && (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>{t('newsfeedNoMatch')}</p>
          )}

          <div className='nf-grid'>
            {pageNews.map(item => (
              <article key={item.id} className='nf-card'>
                {item.photoUrl && (
                  <div className='nf-card__img-wrap'>
                    <img src={item.photoUrl} alt={item.title} className='nf-card__img' />
                  </div>
                )}
                <div className='nf-card__body'>
                  <div className='nf-date'>{formatDate(item.createdAt)}</div>
                  <h2 className='nf-card__title'>{item.title}</h2>
                  {item.content && (
                    <div className='nf-card__content'>
                      <p className='nf-card__text'>
                        {stripMarkdown(item.content).slice(0, 200)}{stripMarkdown(item.content).length > 200 ? '…' : ''}
                      </p>
                      {item.content.length > 200 && (
                        <button className='nf-card__read-more' onClick={() => setActiveItem(item)}>
                          {t('newsfeedReadMore')}
                        </button>
                      )}
                    </div>
                  )}
                  <div className='nf-card__footer'>
                    <LikeButton itemId={item.id} count={item.likesCount} anonUid={anonUid} />
                  </div>
                </div>
              </article>
            ))}
          </div>

          <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={goToPage} />
        </div>
      </div>

      {activeItem && <NewsModal item={activeItem} onClose={() => setActiveItem(null)} anonUid={anonUid} />}
    </section>
  )
}

